import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { LessonDoc, StageProgressEntry } from '../engine/types';
import { LessonView } from './LessonView';

/**
 * §6.5 / §13.4 — compliance and financial teach before they test. A rep works
 * through the lessons, then takes the knowledge check on the same rung.
 *
 * §6.10 / §6.12 — an advanced track is the same screen with a different unit of
 * work. A rung is one thing you pass; a track is a shelf of units, each with its
 * own check, worked through over months. So on a track the check sits inside the
 * lesson that teaches it rather than under all of them, and each unit carries
 * its own state — the shelf is finished when every unit is.
 *
 * Which lessons have been read is held in React state only (§22.2): it is a
 * convenience for the current sitting, not a record. What counts is the check,
 * and the server scores that.
 */
export function KnowledgeStage({
  orgId, stage, title, checkId, progress, unitProgress, byUnit, onTakeCheck, onDone,
}: {
  orgId: string;
  stage: string;
  title: string;
  checkId: string | null;
  progress: StageProgressEntry | null;
  /** §6.10 — per-unit results, keyed by the unit's own check. Tracks only. */
  unitProgress?: Record<string, StageProgressEntry>;
  /** Whether this stage is a shelf of units rather than a single rung. */
  byUnit?: boolean;
  onTakeCheck: (drillId: string) => void;
  onDone: () => void;
}) {
  const [lessons, setLessons] = useState<LessonDoc[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [read, setRead] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, `orgs/${orgId}/lessons`), where('stage', '==', stage)));
        if (cancelled) return;
        // §6.13 — the tactics units are ordered by how easily a competent rep
        // drifts on them, which is the authored order and not the alphabet.
        // Anything unordered sorts after, by title, so an unnumbered lesson is
        // visibly at the end rather than silently first.
        setLessons(snap.docs.map((d) => d.data() as LessonDoc).sort((a, b) => {
          const ao = a.order ?? Number.MAX_SAFE_INTEGER;
          const bo = b.order ?? Number.MAX_SAFE_INTEGER;
          return ao !== bo ? ao - bo : a.title.localeCompare(b.title);
        }));
      } catch (err) {
        console.error('could not load lessons', err);
        if (!cancelled) setError('Those lessons could not be loaded.');
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, stage]);

  const open = (lesson: LessonDoc) => {
    const next = openId === lesson.id ? null : lesson.id;
    setOpenId(next);
    if (next) setRead((prev) => new Set(prev).add(lesson.id));
  };

  const passedUnits = byUnit && lessons
    ? lessons.filter((l) => l.checkDrillId && unitProgress?.[l.checkDrillId]?.passed).length
    : 0;

  return (
    <main className="shell">
      <header className="drill-head">
        <button className="ghost small" onClick={onDone}>Back</button>
        {lessons && (
          <span className="counter">
            {byUnit
              ? `${passedUnits} / ${lessons.length} passed`
              : `${read.size} / ${lessons.length} opened`}
          </span>
        )}
      </header>

      <div>
        <h1>{title}</h1>
        {!byUnit && progress?.passed && (
          <p className="muted">
            Passed{progress.bestScore !== null && ` at ${Math.round(progress.bestScore * 100)}%`}
            {progress.expiresAt && ` · valid until ${new Date(progress.expiresAt).toLocaleDateString()}`}
          </p>
        )}
      </div>

      {error && <p className="error" role="alert">{error}</p>}
      {!lessons && !error && <p className="muted">Loading…</p>}

      {lessons && lessons.length === 0 && (
        <p className="muted">No lessons have been published for this stage yet.</p>
      )}

      <ul className="lesson-list">
        {lessons?.map((lesson) => {
          const unit = lesson.checkDrillId ? unitProgress?.[lesson.checkDrillId] : undefined;
          return (
            <li key={lesson.id} className={`lesson-item ${openId === lesson.id ? 'open' : ''}`}>
              <button className="lesson-toggle" onClick={() => open(lesson)} aria-expanded={openId === lesson.id}>
                <span className="lesson-title">{lesson.title}</span>
                {/* On a track the mark reports a result, not a visit. Reading a
                    unit and passing it are different things, and a checkmark
                    that means "opened" over a shelf of units is a progress bar
                    that fills without measuring anything. */}
                {byUnit
                  ? unit?.passed
                    ? <span className="tag ok">{Math.round((unit.bestScore ?? 0) * 100)}%</span>
                    : unit
                      ? <span className="tag warn">not yet</span>
                      : <span className={`lesson-mark ${read.has(lesson.id) ? 'seen' : ''}`} aria-hidden="true" />
                  : <span className={`lesson-mark ${read.has(lesson.id) ? 'seen' : ''}`} aria-hidden="true" />}
              </button>
              {openId === lesson.id && (
                <>
                  <LessonView lesson={lesson} />
                  {/* The check belongs to the unit it tests, so it is offered
                      where the unit is — not at the bottom of every other one. */}
                  {byUnit && lesson.checkDrillId && (
                    <button className="primary wide" onClick={() => onTakeCheck(lesson.checkDrillId!)}>
                      {unit?.passed ? 'Take this check again' : 'Take the check'}
                    </button>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>

      {!byUnit && (checkId ? (
        <button className="primary wide" onClick={() => onTakeCheck(checkId)}>
          {progress?.passed ? 'Take the check again' : 'Take the check'}
        </button>
      ) : (
        <p className="muted small">The check for this stage has not been published yet.</p>
      ))}
    </main>
  );
}
